import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { AlertButtonRole, AlertController } from 'providers/alert-controller';
import { LoadingController } from 'providers/loading-controller';
import { UserDb } from 'services/user-db.service';
import { AuthService } from 'services/auth.service';
import { LoggingService } from 'services/logging.service';
import { handleRestoreFailureAsync, RestoreDeps, runGuardedRestoreAsync } from 'services/restore-failure';
import { AuthRole } from '@cf-shared/couch-auth';
import { getMyApp } from 'app/app-interface';
import { DbRestoreJsonPageParams } from './db-restore-json-interface';

@Component({
    selector: 'page-db-restore-json',
    templateUrl: 'db-restore-json.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DbRestoreJsonPage extends CsPage<DbRestoreJsonPageParams> {
  protected LL = LL;
  protected pastedJson = '';
  protected parsedDocs: any[] = null;
  protected parseError = '';
  protected loadedFileName = '';

  constructor(
    public navCtrl: NavController,
    private userDb: UserDb,
    private auth: AuthService,
    private alertCtrl: AlertController,
    private loadingCtrl: LoadingController,
    private logging: LoggingService,
    private cdRef: ChangeDetectorRef,
  ) {
    super();
  }

  async ionViewCanEnter() {
    await this.auth.pageAuthGuardAsync(AuthRole.Unknown);
  }

  async ionViewWillEnter() {
    // expose hook for E2E tests to inject fixture JSON without file input
    (window as any).__e2eLoadFixtureJson = (jsonString: string) => {
      this.loadedFileName = 'e2e-fixture.json';
      this.parseJsonString(jsonString);
    };
  }

  async ionViewDidLeave() {
    delete (window as any).__e2eLoadFixtureJson;
  }

  handleChooseFile() {
    const input = document.getElementById('restore-file-input') as HTMLInputElement;
    input.click();
  }

  handleFileChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (!target.files || target.files.length === 0) return;
    const file = target.files[0];
    this.loadedFileName = file.name;
    const reader = new FileReader();
    reader.onload = () => {
      this.parseJsonString(reader.result as string);
      this.safeMarkForCheck(this.cdRef);
    };
    reader.readAsText(file);
  }

  handleParseFromTextarea() {
    this.parseJsonString(this.pastedJson);
  }

  handleRestore() {
    void this.restoreAsync();
  }

  // ---

  private parseJsonString(jsonString: string) {
    try {
      const parsed = JSON.parse(jsonString);
      if (!Array.isArray(parsed)) {
        this.parseError = LL.dbRestoreJson.invalidJson();
        this.parsedDocs = null;
        this.safeMarkForCheck(this.cdRef);
        return;
      }
      // validate each doc has _id
      const allHaveId = parsed.every(doc => doc && typeof doc._id === 'string');
      if (!allHaveId) {
        this.parseError = LL.dbRestoreJson.invalidJson();
        this.parsedDocs = null;
        this.safeMarkForCheck(this.cdRef);
        return;
      }
      this.parsedDocs = parsed;
      this.parseError = '';
    } catch {
      this.parseError = LL.dbRestoreJson.invalidJson();
      this.parsedDocs = null;
    }
    this.safeMarkForCheck(this.cdRef);
  }

  private async restoreAsync() {
    if (!this.parsedDocs || this.parsedDocs.length === 0) return;

    const confirmed = await this.alertCtrl.showAlertAsync<boolean>({
      title: LL.dbRestoreJson.confirmTitle(),
      message: LL.dbRestoreJson.confirmMessage({ count: this.parsedDocs.length }),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel },
        {
          text: LL.dbRestoreJson.restore(),
          role: AlertButtonRole.Primary,
          result: true,
        },
      ],
    });

    if (!confirmed) return;

    // the loading cover and the guarantee that it always comes down live in restore-failure.ts, so
    // this page and the snapshots page cannot drift apart on them
    const restoreError = await runGuardedRestoreAsync(this.restoreDeps(), {
      docs: this.parsedDocs,
      loadingContent: LL.dbRestoreJson.restoring(),
    });

    if (restoreError) {
      await this.handleRestoreFailureAsync(restoreError);
      return;
    }

    // set flag so app.component.ts shows confirmation after reload
    sessionStorage.setItem('json_restored', String(this.parsedDocs.length));

    // Reload to the root page. `resetToRoot` is the part that matters, and it is easy to
    // under-estimate: besides putting `/` in the address bar, it FORGETS the remembered
    // warm-resume address (`warm_resume_url` / `warm_resume_url_ts`). Without that, the boot
    // after this reload restores the address the user was on BEFORE the restore, pointing at
    // data the restore has just replaced -- #5486, where it named a workout that no longer
    // existed, the workout page awaited an alert nobody dismisses, and the boot never finished.
    // Clearing the warm-resume FLAG does not substitute: the cold boot re-sets it before nav
    // init reads it. `sessionStorage` is untouched here, so the flag set just above survives.
    // reloadAppAsync NEVER returns -- it always reaches location.reload().
    await getMyApp().reloadAppAsync({ resetToRoot: true });
  }

  /** What this page's restore needs from the app, handed to the shared restore routines. */
  private restoreDeps(): RestoreDeps {
    return {
      database: this.userDb.database,
      alertCtrl: this.alertCtrl,
      loadingCtrl: this.loadingCtrl,
      logging: this.logging,
    };
  }

  /** Tell the user the restore failed, and reload or deliberately do not — the rules themselves, and
   * the long explanation of why a failure AFTER the remote was replaced must NOT reload, live in
   * `services/restore-failure.ts`. This page supplies only its own telemetry name, its own report
   * context and its own translated wording. */
  private async handleRestoreFailureAsync(err: any) {
    await handleRestoreFailureAsync(this.restoreDeps(), {
      err,
      problemName: 'DbRestoreJsonFailed',
      context: { docCount: this.parsedDocs?.length },
      messages: {
        failedTitle: LL.dbRestoreJson.restoreFailedTitle(),
        failed: LL.dbRestoreJson.restoreFailed(),
        cleanupFailed: LL.dbRestoreJson.restoreCleanupFailed(),
        okButton: { text: LL.common.buttons.ok(), role: AlertButtonRole.Primary },
      },
    });
  }
}
